/**
 * Create / edit / inspect a role.
 *
 * Three modes, decided by the caller:
 *   - create        → `role` undefined
 *   - edit          → `role` set, `role.system` false
 *   - inspect       → `role.system` true, or the viewer is not an admin
 *
 * 🚨 A `system: true` preset is rendered READ-ONLY with no Save button rather
 * than a Save that 403s. Plan §4a: the 4 presets are immutable because 90
 * backend sites reference their ids as string literals.
 */
import React from 'react';
import { X, Loader2, ShieldCheck } from 'lucide-react';
import RolePermissionMatrix from './RolePermissionMatrix';
import { normalizePermissions } from '../../types/rbac';
import type {
  RolePermissions,
  RoleWithPermissions,
  SchemaDescriptor,
} from '../../types/rbac';

interface RoleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Undefined = create a new role. */
  role?: RoleWithPermissions;
  schemas: SchemaDescriptor[];
  /** Read-only when the role is a preset or the viewer cannot manage roles. */
  readOnly: boolean;
  saving: boolean;
  onSave: (body: { name: string; description: string; permissions: RolePermissions }) => void;
}

const RoleEditorModal: React.FC<RoleEditorModalProps> = ({
  isOpen, onClose, role, schemas, readOnly, saving, onSave,
}) => {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [permissions, setPermissions] = React.useState<RolePermissions>({});
  const [error, setError] = React.useState('');

  // Reset whenever the modal opens or the target role changes, so an edit
  // never inherits the previous role's matrix.
  React.useEffect(() => {
    if (!isOpen) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setPermissions(role?.permissions ?? {});
    setError('');
  }, [isOpen, role]);

  if (!isOpen) return null;

  const isPreset = !!role?.system;
  const grantsNothing = Object.keys(normalizePermissions(permissions)).length === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the role a name.');
      return;
    }
    // Re-derive implied view and drop all-false schemas before sending, so the
    // payload matches what the backend stores (omit-never-zero, plan §4).
    onSave({
      name: trimmed,
      description: description.trim(),
      permissions: normalizePermissions(permissions),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg relative animate-fade-in flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-start justify-between p-4 sm:p-6 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white break-words">
              {role ? (readOnly ? role.name : `Edit ${role.name}`) : 'New role'}
            </h2>
            {isPreset && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <ShieldCheck className="w-3.5 h-3.5" />
                Built-in role — its permissions can&apos;t be changed.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-2 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="p-4 sm:p-6 space-y-4 overflow-y-auto min-h-0">
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                id="role-name"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                disabled={readOnly}
                placeholder="Support Lead"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="role-desc" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={readOnly}
                placeholder="What this role is for"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none disabled:opacity-60"
              />
            </div>

            <div>
              <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions
              </p>
              <RolePermissionMatrix
                schemas={schemas}
                permissions={permissions}
                onChange={readOnly ? undefined : setPermissions}
                readOnly={readOnly}
              />
            </div>

            {/* 🚨 Warn before saving a role that grants nothing — it is legal,
                but whoever holds it lands on the no-access page everywhere
                except Settings. Plan §9.1. */}
            {!readOnly && grantsNothing && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                This role grants no pages. Members who hold only this role will
                see Settings and nothing else.
              </p>
            )}

            {error && (
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>

          {/* Footer — stacked at 375px. */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium text-sm"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Saving...' : role ? 'Save changes' : 'Create role'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleEditorModal;
